import type { ReactNode } from 'react';

/**
 * The three layout primitives every wave-2 screen is built from. They exist because the
 * prototype's geometry depends on a chain of flex parents: the screen is a full-height column,
 * the content scrolls inside it, and the action bar sticks to the bottom. Break the chain
 * anywhere and the bottom bar stops sticking — it simply floats wherever the content ended.
 */

/** Full-height flex column. Every pushed screen starts with this. */
export function Screen({ children }: { children: ReactNode }) {
  return <div className="flex h-full min-h-0 flex-col">{children}</div>;
}

/**
 * The scrolling body. `padding: 4px 18px 18px` from `.content-pad` — the top air comes from the
 * top bar's own bottom padding, which is why 4px here is right and 18px would be too much.
 *
 * `max-width` replaces the prototype's fixed 375px phone: 560px for list screens, 420px for
 * auth forms. Those two numbers are already in use — keep them rather than inventing a third.
 */
export function Content({
  children,
  width = 'wide',
  centered = false,
}: {
  children: ReactNode;
  width?: 'wide' | 'narrow';
  centered?: boolean;
}) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      {/* `min-h-full` unconditionally: without it this column has no height of its own, and a
          child asking for `flex-1` — an empty state wanting to sit in the middle of the screen —
          gets nothing and ends up pinned under the top bar. */}
      <div
        className={
          `mx-auto flex min-h-full w-full flex-col px-[18px] pb-[18px] pt-1 ` +
          (width === 'wide' ? 'max-w-[560px] ' : 'max-w-[420px] ') +
          (centered ? 'justify-center' : '')
        }
      >
        {children}
      </div>
    </div>
  );
}

/**
 * The bottom shelf holding a screen's primary action: `padding: 14px 18px 22px`, painted with
 * the page background so content scrolling underneath does not show through.
 *
 * Sticky rather than fixed, so it participates in the column layout instead of overlaying it,
 * and the bottom padding takes the larger of 22px and the home-indicator inset — an installed
 * PWA on iOS otherwise puts the button under the system gesture bar.
 */
export function ActionBar({
  children,
  width = 'wide',
}: {
  children: ReactNode;
  /** Must match the `Content` above it, or the shelf ends up wider than the form it belongs to. */
  width?: 'wide' | 'narrow';
}) {
  return (
    <div
      className="sticky bottom-0 shrink-0 bg-bg px-[18px] pt-3.5"
      style={{ paddingBottom: 'max(22px, env(safe-area-inset-bottom))' }}
    >
      <div
        className={`mx-auto w-full ${width === 'wide' ? 'max-w-[560px]' : 'max-w-[420px]'}`}
      >
        {children}
      </div>
    </div>
  );
}
